import { useParams } from 'react-router-dom';

import { getProjectCohortPath } from '@verantos-dev/core-constants';
import { useCohortProfileQuery } from '@verantos-dev/data-access-cohorts';
import type { PageHeaderBreadcrumb } from '@verantos-dev/shared-components';

import { useProjectRootBreadcrumbs } from './useProjectRootBreadcrumbs';

export interface UseVariableViewBreadcrumbsResult {
  breadcrumbs: PageHeaderBreadcrumb[];
}

/**
 * Per-page breadcrumbs for the project analytical-variable view (OR-6123 F8).
 *
 * The variable view is reachable two ways:
 *   - **cohort-scoped** (`/projects/:pid/cohort/:cid/variable/:avId`) →
 *     `Projects / <project> / <cohort> / <variable>`, with the cohort crumb
 *     linking back to the cohort detail.
 *   - **standalone** (`/projects/:pid/variable/:avId`) →
 *     `Projects / <project> / <variable>`.
 *
 * The cohort name is resolved from the cohort profile only when a `cohortId`
 * is in the URL. Split out of the old monolithic `useProjectBreadcrumbs`.
 *
 * TODO: cleanup with research_assistant_gen_3 ff
 */
export const useVariableViewBreadcrumbs = ({
  variableName,
  isVariableLoading,
}: {
  variableName: string | undefined;
  isVariableLoading: boolean;
}): UseVariableViewBreadcrumbsResult => {
  const { projectId = '', cohortId } = useParams<{ projectId: string; cohortId: string }>();
  const { rootCrumbs } = useProjectRootBreadcrumbs();

  const cohortQuery = useCohortProfileQuery({
    projectId,
    cohortId: cohortId ?? '',
  });

  const crumbs: PageHeaderBreadcrumb[] = [...rootCrumbs];

  if (cohortId) {
    crumbs.push({
      label: cohortQuery.data?.cohortName ?? '',
      href: projectId ? getProjectCohortPath(projectId, cohortId) : undefined,
      isLoading: cohortQuery.isLoading || cohortQuery.isFetching,
    });
  }

  crumbs.push({
    label: variableName ?? '',
    isLoading: isVariableLoading && !variableName,
  });

  return { breadcrumbs: crumbs };
};
